import { supabase } from "@/integrations/supabase/client";
import { StoreTable } from "@/models/tableModel";

export async function fetchTables(storeId: string): Promise<StoreTable[]> {
  try {
    const { data, error } = await supabase
      .from("store_tables")
      .select(
        `
        *,
        staff:assigned_staff_id (
          id,
          first_name,
          last_name,
          role
        )
      `
      )
      .eq("store_id", storeId)
      .order("number", { ascending: true });

    if (error) throw error;

    return data.map((table: any) => ({
      id: table.id,
      store_id: table.store_id,
      number: table.number,
      name: table.name,
      daily_scans: table.daily_scans,
      total_scans: table.total_scans,
      assigned_staff_id: table.assigned_staff_id,
      staffName: table.staff
        ? `${table.staff.first_name} ${table.staff.last_name}`
        : null,
      staffRole: table.staff?.role || null,
      created_at: table.created_at,
      updated_at: table.updated_at,
    }));
  } catch (error) {
    console.error("Error fetching tables:", error);
    return [];
  }
}

export async function fetchTableById(
  tableId: string
): Promise<StoreTable | null> {
  try {
    const { data, error } = await supabase
      .from("store_tables")
      .select("*")
      .eq("id", tableId)
      .single();

    if (error) throw error;
    return data as StoreTable;
  } catch (error) {
    console.error("Error fetching table:", error);
    return null;
  }
}

export function getPublicMenuUrl(tableId: string): string {
  const isDev = process.env.NODE_ENV === "development";
  if (isDev) {
    return `${window.location.origin}/dashboard/menu-sandbox?table=${tableId}`;
  }
  return `${window.location.origin}/menu?table=${tableId}`;
}

export async function createTable(
  tableData: Omit<StoreTable, "id" | "created_at" | "updated_at">
): Promise<StoreTable | null> {
  try {
    const { data, error } = await supabase
      .from("store_tables")
      .insert([tableData])
      .select()
      .single();

    if (error) throw error;
    return data as StoreTable;
  } catch (error) {
    console.error("Error creating table:", error);
    return null;
  }
}

export async function updateTable(
  id: string,
  updates: Partial<StoreTable>
): Promise<StoreTable | null> {
  try {
    const { data, error } = await supabase
      .from("store_tables")
      .update(updates)
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;
    return data as StoreTable;
  } catch (error) {
    console.error("Error updating table:", error);
    return null;
  }
}

export async function deleteTable(id: string): Promise<boolean> {
  try {
    const { error } = await supabase.from("store_tables").delete().eq("id", id);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error("Error deleting table:", error);
    return false;
  }
}

export function subscribeToTableScans(
  tableId: string,
  callback: (table: StoreTable) => void
) {
  return supabase
    .channel(`table-scans-${tableId}`)
    .on(
      "postgres_changes",
      {
        event: "UPDATE",
        schema: "public",
        table: "store_tables",
        filter: `id=eq.${tableId}`,
      },
      (payload) => {
        callback(payload.new as StoreTable);
      }
    )
    .subscribe();
}
